import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Box, Text, Link } from '@chakra-ui/react';
import Gallery, { GalleryImage } from '../components/Gallery';
import HorizontalGallery, { HorizontalGalleryImage } from '../components/HorizontalGallery';
import TextSection from '../components/TextSection/TextSection';
import Breadcrumb from '../components/Breadcrumb';

const TheOldAndalusianWest = () => {
  const { t } = useTranslation('zine');
  const { lng } = useParams<{ lng: string }>();
  const currentLanguage = lng || 'en';

  const getLocalizedPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `/${currentLanguage}${cleanPath}`;
  };
  const wildImages: GalleryImage[] = [
    { src: '/images/wild-west/wild0.jpg', alt: 'The Old Andalusian West film photography - Tabernas Desert cinematic project by Pablo Ledesma' },
  ];

  const lastDawnImages: GalleryImage[] = [
    { src: '/images/wild-west/wild1.jpg', alt: 'The Last Dawn film photography - Western cinematic story Tabernas Desert' },
    { src: '/images/wild-west/wild3.jpg', alt: 'John The Wanderer film photography - Black and white western photography Spain' },
    { src: '/images/wild-west/wild4.jpg', alt: 'Western film photography desert - Analog photography artistic project Andalusia' },
    { src: '/images/wild-west/wild5.jpg', alt: 'Cinematic film photography - Western desert photography Tabernas' },
  ];

  const revengeImages: HorizontalGalleryImage[] = [
    { src: '/images/wild-west/wild6.jpg', alt: 'Revenge of the Solitaire film photography - Western saloon scene analog photography' },
    { src: '/images/wild-west/wild7.jpg', alt: 'Lonesome Jack film photography - Black and white western cinematic story' },
    { src: '/images/wild-west/wild8.jpg', alt: 'Western saloon film photography - Analog photography artistic project Spain' },
    { src: '/images/wild-west/wild9.jpg', alt: 'Cinematic western film photography - Desert story analog photography Andalusia' },
    { src: '/images/wild-west/wild10.jpg', alt: 'Western confrontation film photography - Black and white cinematic photography' },
    { src: '/images/wild-west/wild11.jpg', alt: 'Western film photography finale - Analog photography artistic narrative Tabernas' },
  ];

  const tomLawsonImages: GalleryImage[] = [
    { src: '/images/wild-west/wild12.jpg', alt: 'Tom Lawson Guardian film photography - Western sheriff analog photography Spain' },
    { src: '/images/wild-west/wild13.jpg', alt: 'Desert guardian film photography - Black and white western photography Andalusia' },
    { src: '/images/wild-west/wild14.jpg', alt: 'Western law film photography - Cinematic analog photography Tabernas Desert' },
    { src: '/images/wild-west/wild15.jpg', alt: 'Sheriff film photography - Western character study analog photography Spain' },
  ];

  const sceneryImages: GalleryImage[] = [
    { src: '/images/wild-west/wild16.jpg', alt: 'Western movie set film photography - Tabernas Desert cinematic scenery analog photography' },
    { src: '/images/wild-west/wild17.jpg', alt: 'Abandoned western buildings film photography - Desert architecture analog photography Spain' },
    { src: '/images/wild-west/wild18.jpg', alt: 'Western film set scenery - Cinematic landscape photography Tabernas Desert' },
    { src: '/images/wild-west/wild19.jpg', alt: 'Movie set ruins film photography - Western heritage analog photography Andalusia' },
    { src: '/images/wild-west/wild20.jpg', alt: 'Desert western architecture - Film photography abandoned buildings Spain' },
    { src: '/images/wild-west/wild21.jpg', alt: 'Cinematic western landscape - Tabernas Desert film photography artistic project' },
    { src: '/images/wild-west/wild22.jpg', alt: 'Western movie heritage film photography - Desert scenery analog photography Spain' },
    { src: '/images/wild-west/wild23.jpg', alt: 'Tabernas western film set - Cinematic landscape photography Andalusia desert' },
  ];

  return (
    <Box maxW="1200px" mx="auto" p={4} paddingTop={{ base: "80px", md: "20px" }}>
      {/* Breadcrumb */}
      <Box mb={6}>
        <Breadcrumb 
          items={[
            { label: t('breadcrumb.work'), href: getLocalizedPath('/') },
            { label: t('breadcrumb.zines'), href: getLocalizedPath('/zines') },
            { label: t('breadcrumb.title') }
          ]} 
        />
      </Box>

      <TextSection
        title={t('intro.title')}
        paragraphs={[
          t('intro.content')
        ]}
        layout="side-by-side"
        titlePosition="left"
        titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
      />
      
      <Box mt={8}>
        <Gallery
          images={wildImages}
          columns={{ base: 1, md: 1, lg: 1 }}
          spacing={4}
        />
      </Box>

      <Box mt={12}>
        <TextSection
          title={t('lastDawn.title')}
          paragraphs={[
            t('lastDawn.content')
          ]}
          layout="stacked"
          titlePosition="center"
          titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        />
      </Box>

      <Box mt={8}>
        <Gallery
          images={lastDawnImages}
          columns={{ base: 1, md: 1, lg: 1 }}
          spacing={4}
        />
      </Box>

      <Box mt={12}>
        <TextSection
          title={t('revenge.title')}
          paragraphs={[
            t('revenge.content')
          ]}
          layout="stacked"
          titlePosition="center"
          titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        />
      </Box>

      <Box mt={8}>
        <HorizontalGallery
          images={revengeImages}
          imageHeight="300px"
          spacing={4}
        />
      </Box>

      <Box mt={12}>
        <TextSection
          title={t('guardian.title')}
          paragraphs={[
            t('guardian.content')
          ]}
          layout="stacked"
          titlePosition="center"
          titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        />
      </Box>

      <Box mt={8}>
        <Gallery
          images={tomLawsonImages}
          columns={{ base: 1, md: 2, lg: 2 }}
          spacing={4}
        />
      </Box>

      <Box mt={12}>
        <TextSection
          title={t('scenery.title')}
          paragraphs={[
            t('scenery.content')
          ]}
          layout="stacked"
          titlePosition="center"
          titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        />
      </Box>

      <Box mt={8}>
        <Gallery
          images={sceneryImages}
          columns={{ base: 1, md: 2, lg: 2 }}
          spacing={4}
        />
      </Box>

      <Box mt={12}>
        <TextSection
          title={t('conclusion.title')}
          paragraphs={[
            t('conclusion.content')
          ]}
          layout="stacked"
          titlePosition="center"
          titleSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        />
      </Box>

      <Box mt={8} textAlign="left">
        <Text fontSize="sm" fontWeight="normal" color="gray.600">
          {t('video.description')}{' '}
          <Link 
            href="https://www.youtube.com/watch?v=bSsFUxllw4o" 
            isExternal 
            fontWeight="bold"
            color="gray.800"
            _hover={{ textDecoration: 'underline' }}
          >
            {t('video.linkText')}
          </Link>
          .
        </Text>
      </Box>
    </Box>
  );
};

export default TheOldAndalusianWest;